import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { updateStaff } from "../../../../../helpers/fakebackend_helper";

const UNASSIGNED = "__none__";

/**
 * "Who has which menu?", and change it in place. Joins the staff list to the
 * menu-navigation list on Staff.MenuNav_Id so admins can audit assignments
 * without opening each staff record - and, since Task 24688, reassign from
 * here rather than being sent to the staff record to do it.
 */
export default function UserMenusPanel({ menus, staffList, loading, onChanged }) {
  const [search, setSearch] = useState("");
  const [menuFilter, setMenuFilter] = useState(null); // menu Id string, UNASSIGNED, or null (all)
  // Task 24688 - which row is mid-save, so its picker can be frozen rather
  // than accepting a second change while the first is in flight.
  const [savingId, setSavingId] = useState(null);

  const handleMenuChange = async (row, value) => {
    const next = value === "" ? null : value;
    if (String(next ?? "") === String(row.menuId ?? "")) return;
    setSavingId(row.id);
    try {
      // Same shape the other callers use: /staff/update takes an array of
      // partial rows keyed by Id, so only the one column is sent.
      const res = await updateStaff([{ Id: row.id, MenuNav_Id: next }]);
      if (res?.status === 200) {
        toast.success(
          next
            ? `${row.name || "User"} moved to ${menuNameById[String(next)]}`
            : `${row.name || "User"} now has no menu`
        );
        // The parent owns the staff list, so it has to re-fetch for the row to
        // show the new value.
        onChanged?.();
      } else {
        toast.error(res?.message || "Could not change that menu");
      }
    } catch (e) {
      toast.error("Could not change that menu");
    }
    setSavingId(null);
  };

  // Id -> Name lookup for the assigned menu.
  const menuNameById = useMemo(() => {
    const map = {};
    (menus || []).forEach((m) => {
      map[String(m.Id)] = m.Name;
    });
    return map;
  }, [menus]);

  // Normalise each staff row to the few fields we render.
  const rows = useMemo(() => {
    return (staffList || []).map((s) => {
      const menuId =
        s.MenuNav_Id === null ||
        s.MenuNav_Id === undefined ||
        s.MenuNav_Id === "" ||
        String(s.MenuNav_Id) === "0"
          ? null
          : String(s.MenuNav_Id);
      return {
        id: s.Id,
        name: s.FullName || `${s.FirstName || ""} ${s.LastName || ""}`.trim(),
        jobTitle: s.JobTitle,
        imageUrl: s.ImageUrl,
        menuId,
        menuName: menuId ? menuNameById[menuId] : null,
      };
    });
  }, [staffList, menuNameById]);

  // Count of users per menu (plus the unassigned bucket) for the summary chips.
  const counts = useMemo(() => {
    const c = { [UNASSIGNED]: 0 };
    (menus || []).forEach((m) => {
      c[String(m.Id)] = 0;
    });
    rows.forEach((r) => {
      const key = r.menuId || UNASSIGNED;
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [rows, menus]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (menuFilter === UNASSIGNED) return !r.menuId;
        if (menuFilter) return r.menuId === menuFilter;
        return true;
      })
      .filter((r) => {
        if (!q) return true;
        return (
          r.name?.toLowerCase().includes(q) ||
          r.jobTitle?.toLowerCase().includes(q) ||
          r.menuName?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [rows, search, menuFilter]);

  const toggleFilter = (key) =>
    setMenuFilter((prev) => (prev === key ? null : key));

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        See which navigation menu each user is assigned, and change it here.
        Click a menu below to filter the list.
      </Typography>

      {/* Summary chips — one per menu, plus unassigned. Doubles as a filter. */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {(menus || []).map((m) => {
          const key = String(m.Id);
          return (
            <Chip
              key={key}
              label={`${m.Name} · ${counts[key] || 0}`}
              color={menuFilter === key ? "primary" : "default"}
              variant={menuFilter === key ? "filled" : "outlined"}
              onClick={() => toggleFilter(key)}
              size="small"
            />
          );
        })}
        <Chip
          label={`No menu · ${counts[UNASSIGNED] || 0}`}
          color={menuFilter === UNASSIGNED ? "warning" : "default"}
          variant={menuFilter === UNASSIGNED ? "filled" : "outlined"}
          onClick={() => toggleFilter(UNASSIGNED)}
          size="small"
        />
      </Stack>

      <TextField
        size="small"
        fullWidth
        placeholder="Search users, job titles or menus…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned Menu</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={r.imageUrl} sx={{ width: 28, height: 28 }}>
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {r.name || "—"}
                    </Typography>
                    {/* Task 24688 - open the person's record. Everything else
                        about them - their roles, their details - still lives
                        there, so a list of every user should be able to get
                        you to one. */}
                    <Tooltip title={`Open ${r.name || "staff"} profile`}>
                      <IconButton
                        size="small"
                        component={Link}
                        to={`/staff-details/${r.id}/Overview`}
                      >
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {r.jobTitle || "—"}
                  </Typography>
                </TableCell>
                {/* Task 24688 - "how do i change a role from this screen".
                    You could not: the cell was a chip, and the blurb sent you
                    off to the staff record to do it. This is the screen that
                    lists every user against their menu, so it is the obvious
                    place to change one - and the field behind it is a single
                    column, Staff.MenuNav_Id. */}
                <TableCell>
                  <Select
                    size="small"
                    variant="standard"
                    disableUnderline
                    value={r.menuId || ""}
                    disabled={savingId === r.id}
                    onChange={(e) => handleMenuChange(r, e.target.value)}
                    renderValue={(value) =>
                      value ? (
                        <Chip
                          label={menuNameById[String(value)] || "Unknown menu"}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="No menu"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )
                    }
                    sx={{ "& .MuiSelect-select": { p: 0, pr: 3 } }}
                  >
                    <MenuItem value="">
                      <em>No menu</em>
                    </MenuItem>
                    {(menus || []).map((m) => (
                      <MenuItem key={m.Id} value={String(m.Id)}>
                        {m.Name}
                      </MenuItem>
                    ))}
                  </Select>
                  {savingId === r.id && (
                    <CircularProgress size={12} sx={{ ml: 1 }} />
                  )}
                </TableCell>
              </TableRow>
            ))}

            {!loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Box
                    sx={{
                      py: 5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      color: "text.disabled",
                    }}
                  >
                    <PeopleAltIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="body2">
                      No users match the current filter.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography
                    variant="body2"
                    sx={{ py: 4, textAlign: "center", color: "text.disabled" }}
                  >
                    Loading users…
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}